import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import InfoMessage from '@eika/attention-message/lib/InfoMessage';
import { Button, ButtonGroup, LinkButton, StandardButton } from '@eika/button';
import { ErrorCard } from '@eika/card';
import { IconTypes } from '@eika/icon';
import { RadioButtonPanelOptions, RadioButtonPanels } from '@eika/radio-button';
import { RichIconTypes } from '@eika/rich-icon';

import { useClick2PayCreateProfileMutation } from '@/react-query/hooks/mutation/use-click2pay';
import { useClick2PayEligibleCards } from '@/react-query/hooks/query/use-click2pay';
import { useCurrentCustomer } from '@/react-query/hooks/query/use-customer';

import SkeletonList from '@components/skeleton/SkeletonList';
import useText from '@hooks/use-text';
import { getClick2PayUrl } from '@pages/click2pay/functions';
import Click2PayRadioOptions from '@pages/click2pay/onboarding/choose-card/Click2PayRadioOptions';
import click2PayRoutes from '@pages/click2pay/routes';
import ReplaceCurlyWithJsx from '@utils/replaceCurlyWithJsx';

import './Click2PayChooseCards.scss';

const Click2PayChooseCards = () => {
    const { data: cards, isPending, isError, error } = useClick2PayEligibleCards();
    const [queryParams] = useSearchParams();
    const ecid = queryParams.get('ecid');
    const { addCardToClick2Pay, click2PayCancel, click2PayTitle } = useText();
    const navigate = useNavigate();
    const routes = click2PayRoutes;
    const customer = useCurrentCustomer();
    const text = useText();

    const profileMutation = useClick2PayCreateProfileMutation();

    const [selected, setSelected] = useState<string>(ecid ?? '');

    if (isPending || customer.isPending) {
        return <SkeletonList count={3} />;
    }
    if (isError || customer.isError) {
        return (
            <>
                <ErrorCard
                    title={text.messageErrorUps}
                    icon={RichIconTypes.NETTVERKSBRUDD_HARE}
                    description={text.click2PayGenericError}
                    exception={error ?? undefined}
                />
                <ButtonGroup
                    primaryAction={
                        <StandardButton onClick={() => navigate('/')}>{text.click2PayGoToHome}</StandardButton>
                    }
                />
            </>
        );
    }

    const submit = () => {
        queryParams.set('ecid', selected);
        profileMutation.mutate({
            customer: {
                email: queryParams.get('email') ?? '',
                firstName: customer.data.firstName,
                lastName: customer.data.lastName,
                phoneNumber: customer.data.contact.mobile || '',
            },
            ecid: selected,
        });
        navigate(getClick2PayUrl(queryParams, routes.summary));
    };

    const radioOptions: RadioButtonPanelOptions[] = cards.map((card) => ({
        value: card.eikaCardId,
        label: card.accountName,
        children: <Click2PayRadioOptions card={card} />,
    }));
    return (
        <>
            <h1>{click2PayTitle}</h1>
            <RadioButtonPanels
                label={text.click2PayChooseCardsTitle}
                selectedOption={selected}
                onChange={setSelected}
                className="radio-button-panel"
                options={radioOptions}
            />
            <InfoMessage
                className="click2pay-info-message"
                text={
                    <>
                        {ReplaceCurlyWithJsx(
                            text.click2PayConsentText,
                            <Button
                                buttonType="link"
                                className="click2pay-consent-link"
                                onClick={() => navigate(getClick2PayUrl(queryParams, routes.termsAndConditions))}
                            >
                                våre vilkår for bruk
                            </Button>,
                        )}
                    </>
                }
                icon={IconTypes.INFO_24}
            />
            <ButtonGroup
                primaryAction={
                    <StandardButton onClick={() => submit()} disabled={!selected}>
                        {addCardToClick2Pay}
                    </StandardButton>
                }
                tertiaryAction={<LinkButton onClick={() => navigate('/')}>{click2PayCancel}</LinkButton>}
            />
        </>
    );
};
export default Click2PayChooseCards;
